import React from 'react'
import classnames from 'classnames'
import Link from 'next/link'
import * as Sentry from '@sentry/nextjs'
import {
  Spacer,
  MaxWidthConstraint,
  OvalButton,
  ButtonVariant,
  WorkArea,
} from '@revelrygroup/components'

import EventTrackingModel from '../../models/EventTrackingModel'
import FlowerRow from '../../../components/FlowerRow'
import Obituary from '../../../components/models/Obituary'
import { ObituarySnippet } from '../../../types'
import { routes } from '../../../routes'
import styles from '../styles.module.scss'
import useFlowerProductsContext from '../../../hooks/useFlowerProductsContext'
import useFacebook from '../../../hooks/useFacebook'

interface Props {
  obituary: ObituarySnippet | null
}

const ObituaryPage: React.FC<Props> = ({ obituary }) => {
  useFacebook()
  const { products } = useFlowerProductsContext()
  const fbShare = () => {
    FB.ui(
      {
        method: 'share',
        href: window.location.href,
      },
      (response) => {
        if (!response) return

        if (response.post_id) {
          EventTrackingModel.trackFacebookShare()
        }

        if (response.error_message) {
          Sentry.captureMessage(response.error_message)
        }
      },
    )
  }

  if (!obituary) {
    return null
  }

  const { deceased } = obituary

  return (
    <>
      <WorkArea avoidHeader={true} className={styles.workarea}>
        <MaxWidthConstraint isCentered={true} maxWidth={600}>
          <Spacer verticalBottom="sm">
            <h1 className="h2">
              {Obituary.deceasedFullName(obituary, { includeMiddleName: true })} - Obituary
            </h1>
          </Spacer>

          {deceased.obituary_text ? (
            <>
              <span className={styles.firstLetter}>{deceased.obituary_text[0]}</span>

              <p
                className={classnames('body', styles.obituaryText)}
                dangerouslySetInnerHTML={{ __html: deceased.obituary_text.substring(1) }}
              />
            </>
          ) : (
            <p className={classnames('body', styles.obituaryText)}>
              No obituary has been added at this time.{' '}
              <span className="print-hide">
                Leave a memory or condolence for the family on the{' '}
                <Link href={routes.memories(obituary)}>
                  <a>&quot;Memories&quot; page.</a>
                </Link>
              </span>
            </p>
          )}

          <OvalButton
            as={undefined}
            className={classnames(styles.fbButton, 'print-hide')}
            onClick={fbShare}
            variant={ButtonVariant.DEFAULT_LIGHT}
          >
            Share on Facebook
          </OvalButton>
        </MaxWidthConstraint>
      </WorkArea>

      {Boolean(obituary.enable_flowerstore && products.length) && (
        <FlowerRow obituary={obituary} products={products.slice(0, 11)} />
      )}
    </>
  )
}

export default ObituaryPage
